import { useState } from "react";

import Button from "@/framework/buttons/Button";
import FormCard from "@/framework/forms/FormCard";
import FormInput from "@/framework/forms/FormInput";
import FormPhoneInput from "@/framework/forms/FormPhoneInput";
import FormSection from "@/framework/forms/FormSection";


import { useMaster } from "@/hooks/useMasters";
import { OrderItemsCreate } from "./OrderItemsCreate";
import { OrderCreate, OrderItemCreate } from "@/api";
import { useUserContext } from "@/context/UserContext";

interface OrderCreateFormProps {
  initialData?: OrderCreate;

  getApplicationLabel?: (
    applicationId: number,
  ) => string;

  saving?: boolean;

  onSubmit: (
    order: OrderCreate,
  ) => void | Promise<void>;

  onCancel?: () => void;
}

function createOrderItem(): OrderItemCreate {
  return {
    application_id: 0,
    quantity: 1,
  };
}

export default function OrderCreateForm({
  initialData,
  getApplicationLabel,
  saving = false,
  onSubmit,
  onCancel,
}: OrderCreateFormProps) {

  const [error, setError] = useState<string | null>(null);
  const { basicData } = useUserContext();
  const isAdmin =
    basicData?.role_name?.toUpperCase() === "ADMIN";

  const [form, setForm] = useState<OrderCreate>(() => ({
    requestor_email:
      initialData?.requestor_email ??
      (!isAdmin ? basicData?.email : "") ??
      "",

    requestor_name:
      initialData?.requestor_name ??
      (!isAdmin ? basicData?.name : "") ??
      "",

    requestor_phone:
      initialData?.requestor_phone ?? "",

    items:
      initialData?.items ?? [],
  }));

  const { data: applications = [] } =
    useMaster("application_master");

  const applicationOptions = applications.map((application) => ({
    value: application.id,
    label: application.service_name,
  }));


  function updateForm(
    patch: Partial<OrderCreate>,
  ) {
    setForm((current) => ({
      ...current,
      ...patch,
    }));
  }


  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    setError(null);

    // ---------------------------------------------
    // Validate email
    // ---------------------------------------------

    if (!form.requestor_email?.trim()) {
      setError("Email is required.");
      return;
    }

    // ---------------------------------------------
    // Validate items
    // ---------------------------------------------

    if (!form.items || form.items.length === 0) {
      setError("At least one order item is required.");
      return;
    }

    // ---------------------------------------------
    // Let parent perform the actual creation
    // ---------------------------------------------

    try {
      await onSubmit({
        ...form,
        requestor_name:
          form.requestor_name?.trim() ?? "",
        requestor_email:
          form.requestor_email.trim(),
        requestor_phone:
          form.requestor_phone?.trim() ?? "",
      });
    } catch (err) {
      console.error(
        "Failed to submit order:",
        err
      );

      setError(
        err instanceof Error
          ? err.message
          : "Failed to create order"
      );
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-6">

        {/* =========================================================
          ORDER DETAILS
      ========================================================= */}

        <FormCard>
          <FormSection
            title="Requestor Information"
            description="Provide the details of the person requesting the service."
            columns={2}
          >
            <FormInput
              label="Requestor Name"
              value={form.requestor_name ?? ""}
              required
              placeholder="Enter requestor name"
              onValueChange={(value) =>
                setForm((current) => ({
                  ...current,
                  requestor_name: value,
                }))
              }
            />

            <FormInput
              label="Email"
              value={form.requestor_email ?? ""}
              required
              placeholder="Enter email address"
              onValueChange={(value) =>
                setForm((current) => ({
                  ...current,
                  requestor_email: value,
                }))
              }
            />

            <FormPhoneInput
              label="Phone"
              value={form.requestor_phone ?? ""}
              phoneCode="+91"
              required
              onValueChange={(value) =>
                setForm((current) => ({
                  ...current,
                  requestor_phone: value,
                }))
              }
            />
          </FormSection>
        </FormCard>


        {/* =========================================================
          ORDER ITEMS
      ========================================================= */}
        <OrderItemsCreate
          items={form.items}
          applicationOptions={applicationOptions}
          onChange={(items) =>
            setForm((current) => ({
              ...current,
              items,
            }))
          }
        />

        {/* =========================================================
          FORM ACTIONS
      ========================================================= */}

        <div className="flex justify-end gap-3">

          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
          >
            Cancel
          </Button>

          <Button
            type="submit"
          >
            Create Order
          </Button>

        </div>


        {/* =========================================================
          ERROR
      ========================================================= */}

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {error}
          </div>
        )}

      </div>
    </form>
  );
}